/**
 * Admin → Organisation detail + edit.
 *
 * Edit-mode toggle: when off, fields render as plain text; when on,
 * they swap to inputs and a Save / Cancel pair appears in the header.
 * Domains have their own inline add/remove since they're a separate
 * endpoint and shouldn't be batched into the PATCH.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileSignature, Pencil, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useContracts } from '@/hooks/use-contracts.js';
import {
  useAddOrganizationDomain,
  useOrganization,
  useRemoveOrganizationDomain,
  useUpdateOrganization,
} from '@/hooks/use-organizations.js';

const DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

export function OrganizationDetail() {
  const { orgId } = useParams();
  const orgQuery = useOrganization(orgId);
  const contractsQuery = useContracts();
  const update = useUpdateOrganization();
  const addDomain = useAddOrganizationDomain();
  const removeDomain = useRemoveOrganizationDomain();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [domainDraft, setDomainDraft] = useState('');

  // Reset edit state every time we load a fresh org.
  useEffect(() => {
    if (!orgQuery.data) return;
    setName(orgQuery.data.name);
    setTimezone(orgQuery.data.timezone);
    setContactName(orgQuery.data.primaryContactName ?? '');
    setContactEmail(orgQuery.data.primaryContactEmail ?? '');
    setContactPhone(orgQuery.data.primaryContactPhone ?? '');
  }, [orgQuery.data]);

  if (orgQuery.isLoading) {
    return <OrganizationDetailSkeleton />;
  }
  if (orgQuery.isError || !orgQuery.data) {
    return (
      <div className="p-8 text-ink-300">
        Organisation not found.{' '}
        <Link to="/admin/clients" className="text-beacon-300 hover:underline">
          Back to clients
        </Link>
      </div>
    );
  }

  const org = orgQuery.data;
  const contracts = (contractsQuery.data ?? []).filter(
    (c) => c.organizationId === org.id
  );

  function saveEdits() {
    update.mutate(
      {
        id: org.id,
        input: {
          name: name.trim() || org.name,
          timezone: timezone.trim() || org.timezone,
          primaryContactName: contactName.trim() || null,
          primaryContactEmail: contactEmail.trim().toLowerCase() || null,
          primaryContactPhone: contactPhone.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Organisation updated');
          setEditing(false);
        },
      }
    );
  }

  function cancelEdits() {
    setName(org.name);
    setTimezone(org.timezone);
    setContactName(org.primaryContactName ?? '');
    setContactEmail(org.primaryContactEmail ?? '');
    setContactPhone(org.primaryContactPhone ?? '');
    setEditing(false);
  }

  function commitDomain() {
    const lc = domainDraft.trim().toLowerCase();
    if (!lc) return;
    if (!DOMAIN_RE.test(lc)) {
      toast.error(`"${domainDraft}" doesn't look like a valid domain`);
      return;
    }
    addDomain.mutate(
      { organizationId: org.id, input: { domain: lc } },
      {
        onSuccess: () => {
          setDomainDraft('');
          toast.success(`Added ${lc}`);
        },
      }
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850 sticky top-0 z-10">
        <div className="flex items-center gap-2 text-[12px] text-ink-300 mb-2">
          <Link to="/admin/clients" className="hover:text-ink-100 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Clients
          </Link>
          <span>›</span>
          <span className="text-ink-200">{org.name}</span>
        </div>
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-[19px] font-semibold text-ink-100">
              {org.name}
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <Badge tone={org.type === 'internal' ? 'slate' : 'blue'}>
                {org.type.toUpperCase()}
              </Badge>
              <span className="text-[12px] mono text-ink-400">
                {org.timezone}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={cancelEdits}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveEdits}
                  disabled={update.isPending}
                >
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditing(true)}
              >
                <Pencil size={11} />
                Edit
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="p-6 max-w-3xl space-y-5">
        <Section title="Details">
          <ReadEditField
            label="Name"
            editing={editing}
            value={editing ? name : org.name}
            onChange={setName}
          />
          <ReadEditField
            label="Timezone"
            editing={editing}
            value={editing ? timezone : org.timezone}
            onChange={setTimezone}
            placeholder="Australia/Sydney"
          />
          <ReadEditField
            label="Primary contact name"
            editing={editing}
            value={editing ? contactName : org.primaryContactName ?? ''}
            onChange={setContactName}
            displayPlaceholder="—"
          />
          <ReadEditField
            label="Primary contact email"
            editing={editing}
            value={editing ? contactEmail : org.primaryContactEmail ?? ''}
            onChange={setContactEmail}
            displayPlaceholder="—"
          />
          <ReadEditField
            label="Primary contact phone"
            editing={editing}
            value={editing ? contactPhone : org.primaryContactPhone ?? ''}
            onChange={setContactPhone}
            displayPlaceholder="—"
          />
        </Section>

        <Section title="Email-routing domains">
          {org.domains.length === 0 ? (
            <p className="text-[12.5px] text-ink-400">
              No domains. Inbound email won't auto-route to this organisation
              until at least one is configured.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {org.domains.map((d) => (
                <li
                  key={d.id}
                  className="inline-flex items-center gap-1.5 rounded-md bg-ink-800 border border-ink-600 pl-2 pr-1 py-0.5 text-[12.5px] text-ink-100 mono"
                >
                  {d.domain}
                  <button
                    onClick={() =>
                      removeDomain.mutate(
                        { organizationId: org.id, domainId: d.id },
                        {
                          onSuccess: () =>
                            toast.success(`Removed ${d.domain}`),
                        }
                      )
                    }
                    disabled={removeDomain.isPending}
                    className="text-ink-300 hover:text-ink-100 focus-ring rounded p-0.5 disabled:opacity-50"
                    aria-label={`Remove ${d.domain}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex gap-2">
            <Input
              type="text"
              placeholder="acme.com"
              value={domainDraft}
              disabled={addDomain.isPending}
              onChange={(e) => setDomainDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDomain();
                }
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={commitDomain}
              disabled={!domainDraft.trim() || addDomain.isPending}
            >
              <Plus size={11} />
              Add
            </Button>
          </div>
        </Section>

        <Section
          title={`Contracts (${contracts.length})`}
        >
          {contracts.length === 0 ? (
            <p className="text-[12.5px] text-ink-400">
              No contracts yet for this organisation.
            </p>
          ) : (
            <ul className="divide-y divide-ink-700 rounded-md border border-ink-600 overflow-hidden">
              {contracts.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/admin/clients/${encodeURIComponent(org.id)}/contracts/${encodeURIComponent(c.id)}`}
                    className="flex items-center gap-3 px-3 py-2 text-[13px] hover:bg-ink-800/60 focus-ring"
                  >
                    <FileSignature size={14} className="text-ink-300" />
                    <span className="text-ink-100 font-medium">{c.name}</span>
                    <Badge
                      tone={
                        c.type === 'sla'
                          ? 'blue'
                          : c.type === 'warranty'
                            ? 'violet'
                            : c.type === 'bau'
                              ? 'green'
                              : 'slate'
                      }
                    >
                      {c.type.toUpperCase()}
                    </Badge>
                    <Badge
                      tone={
                        c.status === 'active'
                          ? 'green'
                          : c.status === 'expired'
                            ? 'yellow'
                            : 'neutral'
                      }
                    >
                      {c.status}
                    </Badge>
                    <span className="ml-auto mono text-[12px] text-ink-400">
                      {c.startDate} → {c.endDate}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

function OrganizationDetailSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <header className="px-6 pt-4 pb-3 border-b border-ink-600 bg-ink-850">
        <Skeleton className="h-3 w-40 mb-2" />
        <Skeleton className="h-5 w-1/2 max-w-[360px] mb-2" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-16" inline />
          <Skeleton className="h-3 w-32" inline />
        </div>
      </header>
      <div className="p-6 max-w-3xl space-y-5">
        {(['Details', 'Domains', 'Contracts'] as const).map((label) => (
          <section key={label}>
            <Skeleton className="h-2.5 w-28 mb-2" />
            <div className="rounded-md border border-ink-600 bg-ink-850 p-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[10rem,1fr] items-center gap-3"
                >
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-3/4 max-w-[260px]" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-2">
        {title}
      </h2>
      <div className="rounded-md border border-ink-600 bg-ink-850 p-4 space-y-3">
        {children}
      </div>
    </section>
  );
}

function ReadEditField({
  label,
  editing,
  value,
  onChange,
  placeholder,
  displayPlaceholder,
}: {
  label: string;
  editing: boolean;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  displayPlaceholder?: string;
}) {
  return (
    <div className="grid grid-cols-[10rem,1fr] items-center gap-3">
      <span className="text-[12px] text-ink-300">{label}</span>
      {editing ? (
        <Input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <span className="text-[13px] text-ink-100">
          {value.length > 0 ? value : displayPlaceholder ?? '—'}
        </span>
      )}
    </div>
  );
}
